import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { v4 as uuidv4 } from 'uuid';
import LocalOpsSheet from './LocalOpsSheet';
import PostMediaCarousel from '../post/PostMediaCarousel';
import { normalizeMediaList } from '../../utils/postMediaUtils';
import { getCoarseLocationLabel } from '../../utils/localOpsLocation';
import { getLocalOpsConfig, formatJGK } from '../../utils/localOpsConfig';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import './localops.css';
import '../../styles/jgLocalOpsBadge.css';
import JGLocalOpsMark from '../common/JGLocalOpsMark';

const roundTo = (value, places = 2) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const factor = 10 ** places;
  return Math.round(parsed * factor) / factor;
};

const formatUsd = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '$0.00';
  return `$${roundTo(parsed, 2).toFixed(2)}`;
};

const redactLocationLabel = (label) => {
  if (!label) return '';
  const parts = String(label)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0];
  const looksLikeStreet =
    /\d/.test(first) ||
    /\b(st|street|ave|avenue|rd|road|ln|lane|dr|drive|blvd|boulevard|hwy|highway|pkwy|parkway|ct|court|trl|trail|pl|place)\b/i.test(
      first,
    );
  if (looksLikeStreet && parts.length > 1) {
    return parts.slice(1).join(', ') || 'Nearby';
  }
  if (looksLikeStreet) return 'Nearby';
  return parts.join(', ');
};

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getQuantityAvailable = (offering) => {
  const candidates = [
    offering?.quantityAvailable,
    offering?.availableQuantity,
    offering?.unitsAvailable,
    offering?.stock,
    offering?.inventory,
    offering?.quantity,
    offering?.meta?.quantityAvailable,
    offering?.meta?.availableQuantity,
    offering?.meta?.unitsAvailable,
    offering?.meta?.stock,
    offering?.meta?.inventory,
    offering?.meta?.quantity,
  ];
  for (const value of candidates) {
    const parsed = parseNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

export default function LocalOpsOfferingDetailSheet({
  open,
  offering,
  onClose,
  onMessage,
  onCreateTask,
  onShare,
  onReserve,
  onEdit,
  onEditInventory,
  onPause,
  onMarkAvailability,
  onRemove,
  order,
  orders,
  onFulfillOrder,
  onConfirmOrder,
  onDisputeOrder,
  onCancelOrder,
  presenceEntry,
  currentPlayerId,
  currentPlayerHandle,
  navContext = null,
}) {
  const { player } = useContext(AuthContext);
  const token = player?.token || null;
  const offeringId = offering?.offeringId || offering?.id || offering?._id;
  const mediaList = useMemo(
    () => (offering ? normalizeMediaList(offering) : []),
    [offering],
  );
  const title =
    offering?.title ||
    offering?.name ||
    offering?.meta?.title ||
    'Local Offering';
  const description =
    offering?.description || offering?.meta?.description || '';
  const ownerLabel =
    offering?.handle ||
    offering?.playerId ||
    offering?.owner?.playerId ||
    offering?.creator?.playerId ||
    'Seller';
  const ownerId =
    offering?.ownerId ||
    offering?.owner?._id ||
    offering?.creator?._id ||
    offering?.ownerPlayerId?._id ||
    offering?.providerId ||
    offering?.createdBy ||
    null;
  const isOwner =
    (currentPlayerId && ownerId && String(ownerId) === String(currentPlayerId)) ||
    (currentPlayerHandle &&
      ownerId &&
      String(ownerId) === String(currentPlayerHandle));
  const supportsDelivery =
    offering?.supportsDelivery ||
    offering?.delivery?.supported ||
    offering?.deliverySupported;
  const isService =
    offering?.offeringType === 'service' ||
    offering?.type === 'service' ||
    offering?.meta?.offeringType === 'service';
  const offeringCoordinationCopy = isService
    ? 'Coordinate the service with the seller.'
    : supportsDelivery
    ? 'Coordinate pickup or delivery with the seller.'
    : 'Coordinate pickup with the seller.';
  const reserveSupportCopy = isService
    ? `Your JGK is held after reservation. ${offeringCoordinationCopy} Seller payout is released after you confirm completion.`
    : `Your JGK is held after reservation. ${offeringCoordinationCopy} Seller payout is released after you confirm.`;
  const sellerConfirmationGuidanceCopy = isService
    ? 'Request buyer confirmation after the service is complete. Buyer confirmation releases your payout.'
    : supportsDelivery
    ? 'Request buyer confirmation after pickup or delivery is complete. Buyer confirmation releases your payout.'
    : 'Request buyer confirmation after pickup is complete. Buyer confirmation releases your payout.';
  const buyerReservationNextStepCopy = `Next step: ${offeringCoordinationCopy} You can cancel and return held JGK before the seller requests confirmation.`;
  const buyerConfirmCtaLabel = isService
    ? 'Confirm service complete & release payout'
    : 'Confirm received & release payout';
  const isPaused = offering?.isPaused === true;
  const baseAvailable = useMemo(
    () => getQuantityAvailable(offering),
    [offering],
  );
  const isMarkedUnavailable = isService
    ? offering?.isBooked
    : offering?.isOutOfStock &&
      !(Number.isFinite(baseAvailable) && baseAvailable > 0);
  const isUnavailable = isPaused || isMarkedUnavailable;
  const markLabel = isService
    ? isMarkedUnavailable
      ? 'Mark available'
      : 'Mark booked'
    : isMarkedUnavailable
    ? 'Mark in stock'
    : 'Mark out of stock';
  const availability =
    offering?.availabilityState ||
    presenceEntry?.availabilityState ||
    presenceEntry?.status;
  const ownerPhoto =
    presenceEntry?.profilePhoto ||
    offering?.owner?.profilePhoto ||
    offering?.creator?.profilePhoto ||
    offering?.ownerPhoto ||
    offering?.profilePhoto ||
    offering?.avatar ||
    null;
  const locationLabel = getCoarseLocationLabel(offering) || 'Nearby';
  const [availableOverride, setAvailableOverride] = useState(null);
  const availableUnits =
    Number.isFinite(availableOverride) ? availableOverride : baseAvailable;
  const isSoldOutFlag =
    !isService && Number.isFinite(availableUnits)
      ? availableUnits <= 0
      : isMarkedUnavailable;
  const hasAvailability = Number.isFinite(availableUnits);
  const quantityCap = hasAvailability
    ? Math.max(1, Math.floor(availableUnits))
    : 99;
  const invariantWarnedRef = useRef(new Set());
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'development' ||
      isUnavailable ||
      !hasAvailability
    ) {
      return;
    }
    const offeringId =
      offering?.offeringId || offering?.id || offering?._id || 'unknown';
    if (
      availableUnits <= 0 &&
      offeringId &&
      !invariantWarnedRef.current.has(offeringId)
    ) {
      invariantWarnedRef.current.add(offeringId);
      // eslint-disable-next-line no-console
      console.warn('[localops][invariant] available <= 0 while available', {
        offeringId,
        availableUnits,
        isMarkedUnavailable,
      });
    }
  }, [availableUnits, hasAvailability, isUnavailable, isMarkedUnavailable, offering]);
  const [quantity, setQuantity] = useState(1);
  const [reserveStatus, setReserveStatus] = useState('idle');
  const [reserveError, setReserveError] = useState('');
  const [reservedOrder, setReservedOrder] = useState(null);
  const incomingOrder = order || null;
  const activeOrder = incomingOrder || reservedOrder;
  const localOpsConfig = useMemo(() => getLocalOpsConfig(), []);
  const hasBlockingOrder = activeOrder?.status === 'reserved';
  const sellerOrders = Array.isArray(orders) ? orders : [];
  const ownerReservedEscrowOrder = useMemo(
    () =>
      sellerOrders.find((orderItem) => {
        const status = String(orderItem?.status || '').toLowerCase();
        const escrowStatus = String(orderItem?.escrowStatus || '').toLowerCase();
        // Owner guidance is only shown when a reservation is active and escrow is still held.
        return status === 'reserved' && escrowStatus === 'held';
      }) || null,
    [sellerOrders],
  );
  const isFunded = useMemo(() => {
    if (activeOrder?.escrowStatus === 'held') return true;
    return sellerOrders.some(
      (orderItem) => orderItem?.escrowStatus === 'held',
    );
  }, [activeOrder, sellerOrders]);
  const idempotencyKeyRef = useRef(null);
  const isPriceOnRequest = offering?.requestQuote === true;
  const unitPrice = useMemo(
    () => parseNumber(offering?.price),
    [offering],
  );
  const hasUnitPrice = Number.isFinite(unitPrice) && !isPriceOnRequest;
  const isPaymentReady = hasUnitPrice;
  const isQuoteRequired = isPriceOnRequest;
  const totalPrice = hasUnitPrice ? unitPrice * quantity : null;
  const unitPriceLabel = isPriceOnRequest
    ? 'Contact for price'
    : hasUnitPrice
    ? formatUsd(unitPrice)
    : 'Price unavailable';
  const totalPriceLabel = hasUnitPrice ? formatUsd(totalPrice) : '--';
  const isReserved =
    reserveStatus === 'reserved' ||
    activeOrder?.status === 'reserved';
  const reservedOrderStatus = activeOrder?.status || (isReserved ? 'reserved' : null);
  const orderEscrowStatus = activeOrder?.escrowStatus || null;
  const settlementOutcome = String(
    activeOrder?.settlementOutcome || 'none',
  ).toLowerCase();
  const rawSettlementStatus = String(
    activeOrder?.settlementStatus || activeOrder?.settlement_state || 'none',
  )
    .trim()
    .toLowerCase();
  const settlementStatus =
    rawSettlementStatus && rawSettlementStatus !== 'null'
      ? rawSettlementStatus
      : 'none';
  const showPrivateAddresses = isReserved;
  const displayLocationLabel =
    showPrivateAddresses ? locationLabel : redactLocationLabel(locationLabel) || 'Nearby';
  const isPending = reserveStatus === 'pending';
  const isSoldOut = hasAvailability ? availableUnits <= 0 : isSoldOutFlag;
  const orderStatusLabel = activeOrder?.status || null;
  const hasTerminalActiveOrder =
    !!activeOrder &&
    ([
      'fulfilled',
      'completed',
      'closed',
      'cancelled',
      'canceled',
    ].includes(String(orderStatusLabel || '').toLowerCase()) ||
      ['paid_out', 'released'].includes(
        String(orderEscrowStatus || '').toLowerCase(),
      ) ||
      settlementStatus === 'settled' ||
      ['paid_seller', 'refunded_buyer'].includes(settlementOutcome));
  const confirmByAtMs = activeOrder?.confirmByAt
    ? Date.parse(String(activeOrder.confirmByAt))
    : NaN;
  const confirmationWindowExpired =
    settlementStatus === 'confirmation_requested' &&
    Number.isFinite(confirmByAtMs) &&
    confirmByAtMs < Date.now();
  const effectiveSettlementStatus = confirmationWindowExpired
    ? 'admin_review'
    : settlementStatus;
  const providerConfirmedAt = activeOrder?.providerConfirmedAt || null;
  const hasProviderConfirmation = !!providerConfirmedAt;
  const buyerCanConfirm =
    !isOwner &&
    orderStatusLabel === 'reserved' &&
    orderEscrowStatus === 'held' &&
    effectiveSettlementStatus === 'confirmation_requested' &&
    hasProviderConfirmation;
  const buyerCanDispute =
    !isOwner &&
    orderStatusLabel === 'reserved' &&
    orderEscrowStatus === 'held' &&
    effectiveSettlementStatus === 'confirmation_requested' &&
    hasProviderConfirmation;
  const buyerCanCancelPreConfirmation =
    !isOwner &&
    orderStatusLabel === 'reserved' &&
    ['held', 'none'].includes(String(orderEscrowStatus || 'none')) &&
    effectiveSettlementStatus === 'none' &&
    !hasProviderConfirmation;
  const buyerCanCancelLegacyPreConfirmationIssue =
    !isOwner &&
    orderStatusLabel === 'reserved' &&
    orderEscrowStatus === 'disputed' &&
    effectiveSettlementStatus === 'disputed' &&
    !hasProviderConfirmation &&
    settlementOutcome === 'none';
  const buyerCanCancelReservation =
    buyerCanCancelPreConfirmation || buyerCanCancelLegacyPreConfirmationIssue;
  const orderQuantity = Number.isFinite(activeOrder?.quantity)
    ? activeOrder.quantity
    : null;
  const orderSubtotal = Number.isFinite(activeOrder?.subtotalUsd)
    ? activeOrder.subtotalUsd
    : Number.isFinite(activeOrder?.subtotal)
    ? activeOrder.subtotal
    : null;
  const priceRate =
    Number.isFinite(localOpsConfig?.jgkPerUsd) && localOpsConfig.jgkPerUsd > 0
      ? localOpsConfig.jgkPerUsd
      : 1000;
  const showOfferingEscrow =
    isReserved || isFunded || orderEscrowStatus === 'held';
  const escrowBasisQuantity = Number.isFinite(orderQuantity)
    ? orderQuantity
    : Number.isFinite(quantity)
    ? quantity
    : 1;
  const escrowUsd = showOfferingEscrow
    ? Number.isFinite(orderSubtotal)
      ? orderSubtotal
      : hasUnitPrice
      ? unitPrice * escrowBasisQuantity
      : null
    : null;
  const escrowJgkLabel =
    escrowUsd !== null ? formatJGK(escrowUsd * priceRate) : null;
  const escrowLabel =
    orderEscrowStatus === 'held'
      ? 'JGK held'
      : orderEscrowStatus === 'paid_out'
      ? 'Payout released'
      : orderEscrowStatus === 'released'
      ? 'Held JGK returned'
      : orderEscrowStatus === 'disputed'
      ? 'Problem reported'
      : null;
  const isRefundedTerminalOrder =
    hasTerminalActiveOrder &&
    (['canceled', 'cancelled'].includes(
      String(orderStatusLabel || '').toLowerCase(),
    ) ||
      orderEscrowStatus === 'released' ||
      settlementOutcome === 'refunded_buyer');
  const terminalSoldOutCopy = isRefundedTerminalOrder
    ? `Reservation canceled. Held JGK was returned. No more ${
        isService ? 'sessions' : 'units'
      } are available right now.`
    : `Completed. Seller payout was released. No more ${
        isService ? 'sessions' : 'units'
      } are available right now.`;
  const terminalReserveAnotherCopy = isRefundedTerminalOrder
    ? `Reservation canceled. Held JGK was returned. Reserve another ${
        isService ? 'session' : 'unit'
      } if available.`
    : `Completed. Seller payout was released. Reserve another ${
        isService ? 'session' : 'unit'
      } if available.`;
  const reserveDisabledReason = isPriceOnRequest
    ? 'Contact the seller for price.'
    : !hasUnitPrice
    ? 'Price unavailable.'
    : isOwner
    ? 'You cannot reserve your own offering.'
    : hasBlockingOrder
    ? 'You already have an order for this offering.'
    : isPaused
    ? 'Currently paused.'
    : isMarkedUnavailable
    ? 'Currently unavailable.'
    : isSoldOut
    ? 'Sold out.'
    : '';
  const reserveDisabled =
    !hasUnitPrice ||
    isOwner ||
    hasBlockingOrder ||
    isUnavailable ||
    isSoldOut ||
    isPending ||
    isReserved;
  const stepperDisabled =
    !hasUnitPrice ||
    isOwner ||
    hasBlockingOrder ||
    isUnavailable ||
    isSoldOut ||
    isPending ||
    isReserved;
  const showTerminalSoldOutNotice =
    !isReserved &&
    hasTerminalActiveOrder &&
    (isSoldOut || isMarkedUnavailable);
  const showReservePanel = !isReserved && !showTerminalSoldOutNotice;

  useEffect(() => {
    setQuantity(1);
    setReserveStatus('idle');
    setReserveError('');
    setReservedOrder(null);
    setAvailableOverride(null);
    idempotencyKeyRef.current = null;
  }, [offeringId]);

  useEffect(() => {
    if (!hasAvailability) return;
    setQuantity((prev) =>
      Math.min(Math.max(prev, 1), quantityCap),
    );
  }, [hasAvailability, quantityCap]);

  const clampQuantity = useCallback(
    (value) => {
      const next = Number.isFinite(value) ? Math.floor(value) : 1;
      return Math.min(Math.max(next, 1), quantityCap);
    },
    [quantityCap],
  );

  const handleQuantityChange = useCallback(
    (event) => {
      const next = parseInt(event.target.value, 10);
      setQuantity(clampQuantity(next));
    },
    [clampQuantity],
  );

  const handleDecrease = useCallback(() => {
    setQuantity((prev) => clampQuantity(prev - 1));
  }, [clampQuantity]);

  const handleIncrease = useCallback(() => {
    setQuantity((prev) => clampQuantity(prev + 1));
  }, [clampQuantity]);

  const handleAcceptOffering = useCallback(async () => {
    if (!offeringId || reserveDisabled) return;
    if (!token) {
      setReserveError('Please log in to reserve.');
      return;
    }
    setReserveError('');
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = uuidv4();
    }
    setReserveStatus('pending');
    try {
      let orderId = null;
      let updatedOffering = null;

      if (onReserve) {
        const result = await onReserve({
          offeringId,
          quantity,
          idempotencyKey: idempotencyKeyRef.current,
        });
        orderId = result?.orderId || null;
        updatedOffering = result?.updatedOffering || null;
      } else {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axiosConn.post(
          `local-ops/offerings/${offeringId}/accept`,
          {
            quantity,
            idempotencyKey: idempotencyKeyRef.current,
          },
          { headers },
        );
        orderId =
          res?.data?.orderId ||
          res?.data?.data?.orderId ||
          res?.data?.data?._id ||
          res?.data?._id ||
          null;
        updatedOffering =
          res?.data?.offering ||
          res?.data?.data?.offering ||
          res?.data?.payload?.offering ||
          null;
      }
      const nextAvailable = updatedOffering
        ? getQuantityAvailable(updatedOffering)
        : null;
      setReservedOrder({ orderId, status: 'reserved' });
      setReserveStatus('reserved');
      if (Number.isFinite(nextAvailable)) {
        setAvailableOverride(Math.max(0, nextAvailable));
      } else if (Number.isFinite(availableUnits)) {
        setAvailableOverride(
          Math.max(0, availableUnits - quantity),
        );
      }
    } catch (err) {
      if (err?.code === 'BUYER_PENDING_CONFIRMATION_REQUIRED') {
        setReserveStatus('idle');
        return;
      }
      const status = err?.response?.status;
      const fallbackMessage =
        err?.message || 'Unable to reserve right now.';
      if (status === 409) {
        setReserveError('No longer available.');
        if (Number.isFinite(availableUnits)) {
          setAvailableOverride(0);
        }
      } else {
        setReserveError(fallbackMessage);
      }
      setReserveStatus('idle');
      idempotencyKeyRef.current = null;
    }
  }, [
    offeringId,
    reserveDisabled,
    token,
    quantity,
    onReserve,
    availableUnits,
  ]);

  return (
    <LocalOpsSheet open={open} onClose={onClose} title="Offering">
      <div className="localops-badge-inline">
        <JGLocalOpsMark />
      </div>
      {navContext && navContext.total > 1 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 10,
          }}
        >
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={navContext.onPrev}
          >
            Previous
          </button>
          <div
            style={{
              fontSize: 13,
              color: '#cfd8e8',
              flex: 1,
              textAlign: 'center',
            }}
          >
            {Number(navContext.index ?? 0) + 1} / {navContext.total}
          </div>
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={navContext.onNext}
          >
            Next
          </button>
        </div>
      ) : null}
      <div className="localops-detail-hero">
        <div className="localops-detail-hero__top">
          <div className="localops-chip-group">
            <span className="localops-pill-chip">
              {offering?.category || offering?.type || 'Offering'}
            </span>
            {isOwner ? (
              <span className="localops-pill-chip localops-pill-chip--owner">
                Your offering
              </span>
            ) : null}
            {offering?.featured || offering?.isFeatured ? (
              <span className="localops-pill-chip">Featured</span>
            ) : null}
            {offering?.sponsored || offering?.isSponsored ? (
              <span className="localops-pill-chip">Sponsored</span>
            ) : null}
            {supportsDelivery ? (
              <span className="localops-pill-chip">Delivery</span>
            ) : null}
            {isPaused ? (
              <span className="localops-pill-chip">Paused</span>
            ) : null}
            {isMarkedUnavailable ? (
              <span className="localops-pill-chip">
                {isService ? 'Booked' : 'Out of stock'}
              </span>
            ) : null}
            {isSoldOut && !isMarkedUnavailable ? (
              <span className="localops-pill-chip">Sold out</span>
            ) : null}
            {isReserved ? (
              <span className="localops-pill-chip">Reserved</span>
            ) : null}
          </div>
          {availability ? (
            <div className="localops-presence-chip">
              <span
                className={`localops-presence-dot ${
                  availability === 'busy'
                    ? 'is-busy'
                    : availability === 'idle'
                    ? 'is-idle'
                    : availability === 'offline'
                    ? 'is-offline'
                    : ''
                }`}
              />
              {availability}
            </div>
          ) : null}
        </div>
        <div className="localops-chip-group localops-chip-group--trust">
          <span className="localops-pill-chip localops-pill-chip--verified">
            Verified Local
          </span>
          {isPaymentReady ? (
            <span className="localops-pill-chip localops-pill-chip--ready">
              Payment Ready
            </span>
          ) : isQuoteRequired ? (
            <span className="localops-pill-chip">Quote Required</span>
          ) : null}
          {isFunded ? (
            <span className="localops-pill-chip localops-pill-chip--funded">
              Funded
            </span>
          ) : null}
        </div>
        <div className="localops-owner-row">
          <span className="localops-avatar">
            {ownerPhoto ? (
              <img src={ownerPhoto} alt="" />
            ) : (
              <span className="localops-avatar__fallback">
                {String(ownerLabel || '?').charAt(0).toUpperCase()}
              </span>
            )}
            {availability ? (
              <span
                className={`localops-presence-dot ${
                  availability === 'busy'
                    ? 'is-busy'
                    : availability === 'idle'
                    ? 'is-idle'
                    : availability === 'offline'
                    ? 'is-offline'
                    : ''
                }`}
              />
            ) : null}
          </span>
          <div>
            <div className="localops-owner-title">
              {ownerLabel}
              {isOwner ? ' (You)' : ''}
            </div>
            <div className="localops-helper">Local seller</div>
          </div>
        </div>
        <h3 className="localops-detail-title">{title}</h3>
        <div className="localops-detail-sub">{displayLocationLabel}</div>
        <div className="localops-detail-sub">
          Pickup address is shared after a reservation.
        </div>
        {hasUnitPrice ? (
          <div className="localops-price-row" style={{ marginTop: 12, marginBottom: 8 }}>
            <div className="localops-detail-sub" style={{ fontWeight: 700 }}>
              {`Price: ${formatUsd(unitPrice)}`}
            </div>
            {showOfferingEscrow && escrowJgkLabel ? (
              <div className="localops-helper">
                JGK held: {escrowJgkLabel}
              </div>
            ) : null}
          </div>
        ) : null}
        {description ? (
          <p className="localops-detail-desc">{description}</p>
        ) : null}
      </div>
      {mediaList.length ? (
        <div className="localops-media-hero">
          <PostMediaCarousel
            item={offering}
            autoPlay={false}
            enableZoom
            maxHeight="320px"
          />
        </div>
      ) : null}
      <div className="localops-actions">
        {isOwner ? (
          <>
            {ownerReservedEscrowOrder ? (
              <div className="localops-order-panel">
                <div className="localops-order-panel__header">
                  <div className="localops-order-panel__title">
                    Buyer reserved — confirmation needed
                  </div>
                </div>
                <div className="localops-order-panel__body">
                  {/* Copy-only refinement for owner reserved state: trust-aligned escrow + action guidance. */}
                  <div className="localops-helper">
                    This buyer has reserved your offering.
                  </div>
                  <div className="localops-helper">
                    Buyer JGK is held. You are not paid yet.
                  </div>
                  <div className="localops-helper">
                    {sellerConfirmationGuidanceCopy}
                  </div>
                </div>
              </div>
            ) : null}
            <button
              type="button"
              className="localops-btn localops-btn--ghost"
              onClick={() => onEdit?.(offering)}
            >
              Edit details
            </button>
            {onEditInventory ? (
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={() => onEditInventory?.(offering)}
              >
                Update inventory
              </button>
            ) : null}
            <button
              type="button"
              className="localops-btn localops-btn--ghost"
              onClick={() => onPause?.(offering)}
            >
              {offering?.isPaused ? 'Resume' : 'Pause'}
            </button>
            {onMarkAvailability ? (
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={() => onMarkAvailability?.(offering)}
              >
                {markLabel}
              </button>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                className="localops-btn localops-btn--danger"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    const confirmed = window.confirm(
                      'Remove this offering from the map?',
                    );
                    if (!confirmed) return;
                  }
                  onRemove?.(offering);
                }}
              >
                Remove listing
              </button>
            ) : null}
            {sellerOrders.length ? (
              <div className="localops-order-list">
                <div className="localops-order-list__title">Orders</div>
                {sellerOrders.map((orderItem, index) => {
                  const orderId =
                    orderItem?.orderId || orderItem?._id || orderItem?.id;
                  const buyer = orderItem?.buyer || {};
                  const buyerLabel = buyer?.playerId || 'Buyer';
                  const buyerPhoto = buyer?.profilePhoto || null;
                  const status = orderItem?.status || 'reserved';
                  const escrow = orderItem?.escrowStatus || null;
                  const rawOrderSettlementStatus = String(
                    orderItem?.settlementStatus || 'none',
                  )
                    .trim()
                    .toLowerCase();
                  const orderSettlementStatus =
                    rawOrderSettlementStatus && rawOrderSettlementStatus !== 'null'
                      ? rawOrderSettlementStatus
                      : 'none';
                  const quantity = Number.isFinite(orderItem?.quantity)
                    ? orderItem.quantity
                    : null;
                  const orderSettlementOutcome = String(
                    orderItem?.settlementOutcome || 'none',
                  )
                    .trim()
                    .toLowerCase();
                  const orderHasProviderConfirmation =
                    !!orderItem?.providerConfirmedAt;
                  const sellerPreConfirmationIssue =
                    status === 'reserved' &&
                    escrow === 'disputed' &&
                    orderSettlementStatus === 'disputed' &&
                    !orderHasProviderConfirmation &&
                    orderSettlementOutcome === 'none';
                  const sellerTrueDispute =
                    status === 'reserved' &&
                    orderSettlementStatus === 'disputed' &&
                    orderHasProviderConfirmation;
                  const sellerRefundedOrder =
                    ['canceled', 'cancelled'].includes(
                      String(status || '').toLowerCase(),
                    ) ||
                    escrow === 'released' ||
                    orderSettlementOutcome === 'refunded_buyer';
                  const canRequestConfirmation =
                    status === 'reserved' &&
                    escrow === 'held' &&
                    orderSettlementStatus === 'none';
                  return (
                    <div
                      key={orderId || `order-${index}`}
                      className="localops-order-card"
                    >
                      <div className="localops-order-card__main">
                        <span className="localops-avatar">
                          {buyerPhoto ? (
                            <img src={buyerPhoto} alt="" />
                          ) : (
                            <span className="localops-avatar__fallback">
                              {String(buyerLabel || '?')
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          )}
                        </span>
                        <div>
                          <div className="localops-order-card__title">
                            {buyerLabel}
                          </div>
                          <div className="localops-helper">
                            {quantity ? `${quantity} requested` : 'Order placed'} -{' '}
                            {status}
                          </div>
                          {sellerPreConfirmationIssue ? (
                            <div className="localops-helper">
                              This issue happened before seller confirmation. The buyer can cancel and return held JGK. Seller payout is not available before confirmation.
                            </div>
                          ) : null}
                          {sellerRefundedOrder ? (
                            <div className="localops-helper">
                              Reservation canceled. Held JGK returned to buyer. Returned units can be reserved again when available.
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="localops-order-card__meta">
                        {escrow ? (
                          <span className="localops-pill-chip">{escrow}</span>
                        ) : null}
                        <button
                          type="button"
                          className="localops-btn"
                          disabled={!canRequestConfirmation}
                          onClick={() => onFulfillOrder?.(orderItem)}
                        >
                          {canRequestConfirmation
                            ? 'Request buyer confirmation'
                            : sellerPreConfirmationIssue
                            ? 'Pre-confirmation issue'
                            : sellerTrueDispute
                            ? 'Issue reported'
                            : orderSettlementStatus === 'confirmation_requested'
                            ? 'Waiting for buyer confirmation'
                            : orderSettlementStatus === 'admin_review'
                            ? 'Admin Review Required'
                            : sellerRefundedOrder
                            ? 'Refunded'
                            : status === 'fulfilled'
                            ? 'Fulfilled'
                            : 'Fulfill'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <button
              type="button"
              className="localops-btn"
              onClick={() => onShare?.(offering)}
            >
              Share & Earn JGK
            </button>
            <div className="localops-earn-row">
              <CheckCircleOutlineIcon
                className="localops-earn-icon"
                fontSize="small"
              />
              <span>Nice! Earn JGK Balance for sharing LocalOps.</span>
            </div>
          </>
        ) : (
          <>
            {activeOrder ? (
              <div className="localops-order-panel">
                <div className="localops-order-panel__header">
                  <div>
                    <div className="localops-order-panel__title">Your reservation</div>
                    {orderStatusLabel ? (
                      <div className="localops-helper">
                        Status: {orderStatusLabel}
                      </div>
                    ) : null}
                  </div>
                  {orderStatusLabel ? (
                    <span className="localops-pill-chip">{orderStatusLabel}</span>
                  ) : null}
                </div>
                <div className="localops-order-panel__body">
                  {orderQuantity ? (
                    <div className="localops-helper">
                      Quantity: {orderQuantity}
                    </div>
                  ) : null}
                  {orderSubtotal !== null ? (
                    <div className="localops-helper">
                      Subtotal: {formatUsd(orderSubtotal)}
                    </div>
                  ) : null}
                  {escrowLabel ? (
                    <div className="localops-helper">{escrowLabel}</div>
                  ) : null}
                  {effectiveSettlementStatus === 'confirmation_requested' ? (
                    <div className="localops-helper">
                      Confirm only after you received what was promised. Confirmation releases payout to the seller. Report a problem if something is wrong.
                    </div>
                  ) : null}
                  {buyerCanCancelLegacyPreConfirmationIssue ? (
                    <div className="localops-helper">
                      This issue happened before seller confirmation. Seller payout is not available. Cancel to return held JGK and reopen the offering.
                    </div>
                  ) : null}
                  {effectiveSettlementStatus === 'admin_review' ? (
                    <div className="localops-helper">
                      Confirmation window expired. Admin review is required.
                    </div>
                  ) : null}
                  {orderStatusLabel === 'reserved' &&
                  effectiveSettlementStatus === 'none' ? (
                    <div className="localops-helper">
                      {buyerReservationNextStepCopy}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {isReserved &&
            (buyerCanConfirm || buyerCanDispute || buyerCanCancelReservation) ? (
              <div className="localops-order-panel">
                <div className="localops-order-panel__header">
                  <div className="localops-order-panel__title">
                    {buyerCanCancelReservation
                      ? 'Reservation Actions'
                      : 'Settlement Actions'}
                  </div>
                </div>
                <div className="localops-order-panel__body">
                  {buyerCanCancelReservation ? (
                    <button
                      type="button"
                      className="localops-btn"
                      onClick={() => onCancelOrder?.(activeOrder)}
                    >
                      {buyerCanCancelLegacyPreConfirmationIssue
                        ? 'Cancel reservation and return held JGK'
                        : 'Cancel reservation and return held JGK'}
                    </button>
                  ) : null}
                  {buyerCanConfirm ? (
                    <button
                      type="button"
                      className="localops-btn"
                      onClick={() => onConfirmOrder?.(activeOrder)}
                    >
                      {buyerConfirmCtaLabel}
                    </button>
                  ) : null}
                  {buyerCanDispute ? (
                    <div className="localops-helper">
                      Reporting a problem keeps JGK held and sends this reservation to review.
                    </div>
                  ) : null}
                  {buyerCanDispute ? (
                    <button
                      type="button"
                      className="localops-btn localops-btn--ghost"
                      onClick={() => onDisputeOrder?.(activeOrder)}
                    >
                      Report a problem
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {showTerminalSoldOutNotice ? (
              <div className="localops-helper">
                {terminalSoldOutCopy}
              </div>
            ) : null}
            {showReservePanel ? (
              <div className="localops-reserve-panel">
                <div className="localops-reserve-row">
                  <div>
                    <div className="localops-reserve-title">
                      {hasTerminalActiveOrder ? 'Reserve another' : 'Reserve'}
                    </div>
                    {hasAvailability ? (
                      <div className="localops-helper">
                        {availableUnits} available
                      </div>
                    ) : null}
                  </div>
                  {isReserved ? (
                    <span className="localops-pill-chip">Reserved</span>
                  ) : null}
                </div>
                <div className="localops-reserve-row">
                  <div className="localops-reserve-label">Quantity</div>
                  <div className="localops-quantity-stepper">
                    <button
                      type="button"
                      className="localops-stepper-btn"
                      onClick={handleDecrease}
                      disabled={stepperDisabled || quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={quantityCap}
                      value={quantity}
                      onChange={handleQuantityChange}
                      disabled={stepperDisabled}
                      aria-label="Quantity"
                    />
                    <button
                      type="button"
                      className="localops-stepper-btn"
                      onClick={handleIncrease}
                      disabled={
                        stepperDisabled || quantity >= quantityCap
                      }
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="localops-reserve-summary">
                  <div>Unit price</div>
                  <div>{unitPriceLabel}</div>
                  <div>Total</div>
                  <div>{totalPriceLabel}</div>
                </div>
                {isPriceOnRequest ? (
                  <div className="localops-helper">
                    Contact the seller for price before reserving.
                  </div>
                ) : !hasUnitPrice ? (
                  <div className="localops-helper">Price unavailable</div>
                ) : null}
                {reserveError ? (
                  <div className="localops-reserve-error">
                    {reserveError}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="localops-btn"
                  onClick={handleAcceptOffering}
                  disabled={reserveDisabled}
                  aria-busy={isPending}
                  title={reserveDisabled ? reserveDisabledReason : ''}
                >
                  {isReserved
                    ? 'Reserved'
                    : isPending
                    ? 'Reserving...'
                    : 'Reserve'}
                </button>
                {isSoldOut || isMarkedUnavailable ? (
                  <div className="localops-helper">
                    {isService ? 'Currently booked.' : 'Sold out.'}
                  </div>
                ) : hasTerminalActiveOrder ? (
                  <div className="localops-helper">
                    {terminalReserveAnotherCopy}
                  </div>
                ) : (
                  <div className="localops-helper">
                    {reserveSupportCopy}
                  </div>
                )}
              </div>
            ) : null}
            {isReserved ? (
              <>
                <button
                  type="button"
                  className="localops-btn localops-btn--ghost"
                  onClick={() => onMessage?.(offering)}
                >
                  Message seller
                </button>
                <button
                  type="button"
                  className="localops-btn localops-btn--ghost"
                  onClick={() => onCreateTask?.(offering, 'pickup')}
                >
                  Create Pickup Task
                </button>
                {supportsDelivery ? (
                  <button
                    type="button"
                    className="localops-btn"
                    onClick={() => onCreateTask?.(offering, 'delivery')}
                  >
                    Create Delivery Task
                  </button>
                ) : null}
              </>
            ) : null}
            <button
              type="button"
              className="localops-btn localops-btn--ghost"
              onClick={() => onShare?.(offering)}
            >
              Share & Earn JGK
            </button>
            <div className="localops-earn-row">
              <CheckCircleOutlineIcon
                className="localops-earn-icon"
                fontSize="small"
              />
              <span>Nice! Earn JGK Balance for sharing LocalOps.</span>
            </div>
          </>
        )}
      </div>
    </LocalOpsSheet>
  );
}
